import React, { useState } from 'react';
import { Box, Button, Typography, TextField, Drawer, styled, IconButton, Stack, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import CustomModal from '../../CustomModal';
import userLock from '../../../assets/user-lock.png';
import Image from '../../Image';
import useLocales from '../../../hooks/useLocales';
import Iconify from '../../Iconify';
import NonRootHeader from '../../../layouts/dashboard/header/NonRootHeader';
import useResponsive from '../../../hooks/useResponsive';
import Page from '../../Page';
import { isSafariOrIOS } from '../../../utils/isSafariOrIOS';
import { conObjForm } from '../../../utils/connection-assistant';

const Rule = ({ valid, text }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      color: valid ? '#16A34A' : '#9CA3AF',
      fontWeight: valid ? 600 : 400,
      transition: 'all 0.2s ease',
    }}
  >
    • {text}
  </Typography>
);

const inputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#F3F4F6',
    '& fieldset': { border: '1px solid transparent' },
    '&:hover fieldset': { border: '1px solid transparent' },
    '&.Mui-focused fieldset': { border: '1px solid #D1D5DB' },
  },
  '& input': {
    padding: '12px 16px',
    fontSize: '14px',
  },
  '& input::placeholder': {
    color: '#9CA3AF',
    opacity: 1,
  },
};

const ConfirmButton = styled(Button)(({ theme }) => ({
  flex: 1,
  height: '50px',
  borderRadius: '25px',
  background: theme.palette.gradients.buttonPrimary,
  color: '#fff',
  fontWeight: 700,
  fontSize: '16px',
  textTransform: 'none',
  boxShadow: 'none',
  cursor: 'pointer',
  '&:hover': {
    background: theme.palette.gradients.buttonPrimary,
    filter: 'brightness(0.95)',
  },

  '&:disabled': {
    background: '#E5E7EB',
    color: '#a0a1a3',
  },
}));

const NewPasswordDialog = ({ handleClose, handleOpen }) => {
  const isDesktop = useResponsive('up', 'lg');
  const navigate = useNavigate();
  const { translate } = useLocales();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const otp = useSelector((state) => state.profile.data);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const isMinLength = password.length > 6;
  const hasLetterAndNumber = /[A-Za-z]/.test(password) && /\d/.test(password);
  const passwordsMatch = password && password === confirmPassword;
  const theme = useTheme();

  const isValid = isMinLength && hasLetterAndNumber && passwordsMatch;

  const handleConfirm = async () => {
    setLoading(true);

    try {
      const payload = {
        'reset_user_password[new_password]': password,
        'reset_user_password[verification_code]': otp,
      };

      const { data: response } = await conObjForm.post('v1/reset-password', payload);

      if (response?.success) {
        enqueueSnackbar(response?.message || 'Password changed successfully', {
          variant: 'success',
        });

        handleClose();
        navigate('/');
      } else {
        enqueueSnackbar(response?.message || 'Something went wrong', {
          variant: 'error',
        });
      }
    } catch (error) {
      const data = error.response?.data;

      if (data?.error && Array.isArray(data.error)) {
        data.error.forEach((err) => {
          enqueueSnackbar(err, { variant: 'error' });
        });
      } else if (data?.message) {
        enqueueSnackbar(data.message, { variant: 'error' });
      } else {
        enqueueSnackbar('An error occurred during submission', {
          variant: 'error',
        });
      }

      console.log('error', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isDesktop && (
        <CustomModal
          hasClose
          maxWidth="xs"
          title={translate('reset_password')}
          open={handleOpen}
          onClose={handleClose}
          actions={
            <ConfirmButton variant="contained" fullWidth disabled={!isValid || loading} onClick={handleConfirm}>
              {translate('confirm')}
            </ConfirmButton>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2, px: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Image src={userLock} style={{ width: 110 }} alt="reset-password" />
            </Box>

            <Typography textAlign={'center'} fontWeight={'bold'} fontSize={'24px'}>
              {translate('reset_password')}
            </Typography>
            <Typography textAlign={'center'} fontWeight={500} fontSize={'16px'}>
              {translate('please_enter_new_password')}
            </Typography>

            <TextField
              type="password"
              placeholder={translate('enter_new_password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              size="small"
              sx={inputStyles}
            />

            <TextField
              type="password"
              placeholder={translate('confirm_new_password')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              size="small"
              sx={inputStyles}
            />

            <Typography variant="caption">{translate('password_contain')}</Typography>

            <Box sx={{ fontSize: 13 }}>
              <Rule valid={isMinLength} text={translate('six_characters')} />
              <Rule valid={hasLetterAndNumber} text={translate('contains_letters_numbers')} />
            </Box>
          </Box>
        </CustomModal>
      )}

      {!isDesktop && (
        <Drawer
          anchor="right"
          maxWidth="xs"
          open={handleOpen}
          PaperProps={{
            sx: {
              width: '100%',
            },
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <IconButton onClick={handleClose} sx={{ position: 'absolute', left: 8 }}>
              <Iconify icon="mdi:close" />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              {translate('reset-password')}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              // eslint-disable-next-line no-nested-ternary
              background: !isDesktop
                ? isSafariOrIOS
                  ? theme.palette.gradients.theme3Ios
                  : theme.palette.gradients.theme3
                : '#F6F7F9',
            }}
          >
            <Page
              title="New Password"
              sx={{
                height: '100%',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: { xs: '100%', sm: 400, md: '100%' },
                    maxWidth: { xs: 'unset', sm: 400 },
                    mx: 'auto',
                    mt: 1,
                    // transform: {
                    //   xs: 'scale(0.8)',
                    //   sm: 'scale(0.9)',
                    //   md: 'scale(1)',
                    // },
                    transformOrigin: 'center center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box sx={{ p: 3, borderRadius: 1, backgroundColor: 'white', width: { xs: '80%', sm: 'unset' } }}>
                    <img
                      src={userLock}
                      alt="login-icon"
                      style={{ width: '30vw', maxWidth: 160, margin: '0 auto 24px', display: 'block' }}
                    />

                    <Stack spacing={2}>
                      <Box display={'flex'} flexDirection={'column'} gap={'10px'}>
                        <TextField
                          type="password"
                          placeholder={translate('enter_new_password')}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          fullWidth
                          size="small"
                          sx={inputStyles}
                        />

                        <TextField
                          type="password"
                          placeholder={translate('confirm_new_password')}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          fullWidth
                          size="small"
                          sx={inputStyles}
                        />

                        <Typography variant="caption">{translate('password_contain')}</Typography>

                        <Box sx={{ fontSize: 13 }}>
                          <Rule valid={isMinLength} text={translate('six_characters')} />
                          <Rule valid={hasLetterAndNumber} text={translate('contains_letters_numbers')} />
                        </Box>

                        <ConfirmButton
                          variant="contained"
                          fullWidth
                          disabled={!isValid || loading}
                          onClick={handleConfirm}
                        >
                          {translate('confirm')}
                        </ConfirmButton>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Page>
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default NewPasswordDialog;
